// src/services/systemAnalytics.service.ts

import api from './api';
import type { ApiResponse } from '../types/api.types';

// System Analytics Types - Updated for new API structure
export interface SystemOverviewData {
  totalRegions: number;
  totalProvinces: number;
  totalCommunes: number;
  totalVillages: number;
  totalGroups: number;
  totalMembers: number;
  financialSummary: {
    totalSavingsAmount: number;
    totalWithdrawalsAmount: number;
    netSavingsAmount: number;
    totalSavingsDeposits: number;
    totalSavingsWithdrawals: number;
    totalCredits: number;
    totalCreditsAmount: number;
    totalOutstandingBalance: number;
    averageSavingsPerMember: number;
    averageCreditPerMember: number;
  };
  regionalBreakdown: Array<{
    regionId: string;
    regionName: string;
    regionCode: string;
    totalProvinces: number;
    totalCommunes: number;
    totalVillages: number;
    totalGroups: number;
    totalMembers: number;
    totalSavings: number;
    averageSavingsPerMember: number;
  }>;
}

export interface CommunesOverviewData {
  totalCommunes: number;
  activeCommunes: number;
  communesWithGroups: number;
  totalGroupsAcrossCommunes: number;
  totalMembersAcrossCommunes: number;
  topPerformingCommunes: Array<{
    communeId: string;
    communeName: string;
    communeCode: string;
    provinceName: string;
    regionName: string;
    totalVillages: number;
    totalGroups: number;
    totalMembers: number;
    totalSavings: number;
    savingsTransactions: number;
    performanceScore: number;
  }>;
}

export interface WeeklyContributionsData {
  totalContributions: number;
  totalExpectedAmount: number;
  totalCollectedAmount: number;
  collectionRate: number;
  totalGroups: number;
  totalMembers: number;
  communeBreakdown: Array<{
    communeId: string;
    communeName: string;
    region: string;
    totalContributions: number;
    totalExpected: number;
    totalCollected: number;
    collectionRate: number;
    totalGroups: number;
  }>;
  regionalBreakdown: Array<{
    region: string;
    totalContributions: number;
    totalExpected: number;
    totalCollected: number;
    collectionRate: number;
    communeCount: number;
    totalGroups: number;
  }>;
}

export interface SolidarityContributionsData {
  totalContributions: number;
  totalAmount: number;
  totalGroups: number;
  totalMembers: number;
  averageContribution: number;
  communeBreakdown: Array<{
    communeId: string;
    communeName: string;
    region: string;
    totalContributions: number;
    totalAmount: number;
    averageAmount: number;
    totalGroups: number;
  }>;
}

export interface SystemCreditsData {
  totalCredits: number;
  totalAmount: number;
  totalOutstanding: number;
  totalGroups: number;
  totalMembers: number;
  averageCreditAmount: number;
  statusBreakdown: Array<{
    status: string;
    count: number;
    totalAmount: number;
    totalOutstanding: number;
  }>;
  communeBreakdown: Array<{
    communeId: string;
    communeName: string;
    region: string;
    totalCredits: number;
    totalAmount: number;
    totalOutstanding: number;
    totalGroups: number;
  }>;
}

export interface GroupActivitiesData {
  totalActivities: number;
  totalIncome: number;
  totalExpense: number;
  totalNetIncome: number;
  totalGroups: number;
  averageIncomePerActivity: number;
  typeBreakdown: Array<{
    activityType: string;
    count: number;
    totalIncome: number;
    totalNetIncome: number;
    averageIncome: number;
  }>;
}

export interface OtherIncomeData {
  totalRecords: number;
  totalAmount: number;
  totalGroups: number;
  averageAmount: number;
  sourceBreakdown: Array<{
    source: string;
    count: number;
    totalAmount: number;
    percentage: number;
  }>;
}

export interface SystemSavingsData {
  totalTransactions: number;
  totalAmount: number;
  totalMembers: number;
  averageTransactionAmount: number;
  typeBreakdown: Array<{
    transactionType: string;
    count: number;
    totalAmount: number;
    percentage: number;
  }>;
}

export interface SystemAnalyticsFilters {
  startDate?: string;
  endDate?: string;
  region?: string;
  status?: string;
  activityType?: string;
  source?: string;
  transactionType?: string;
  page?: number;
  limit?: number;
}

class SystemAnalyticsService {
  private readonly BASE_URL = '/analytics';

  /**
   * Get system dashboard overview with regional breakdown
   */
  async getSystemDashboard(
    filters: SystemAnalyticsFilters = {}
  ): Promise<ApiResponse<SystemOverviewData>> {
    const params = new URLSearchParams();
    
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.region) params.append('region', filters.region);

    const response = await api.get(`${this.BASE_URL}/system/dashboard?${params.toString()}`);
    return response;
  }

  /**
   * Get system-wide communes overview
   */
  async getCommunesOverview(
    filters: SystemAnalyticsFilters = {}
  ): Promise<ApiResponse<CommunesOverviewData>> {
    const params = new URLSearchParams();
    
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.region) params.append('region', filters.region);

    const response = await api.get(`${this.BASE_URL}/system/communes?${params.toString()}`);
    return response;
  }

  /**
   * Get commune dashboard data (detailed analytics for a specific commune)
   */
  async getCommuneDashboard(
    communeId: string,
    filters: SystemAnalyticsFilters = {}
  ): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();
    
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);

    const response = await api.get(`${this.BASE_URL}/communes/${communeId}/dashboard?${params.toString()}`);
    return response;
  }

  /**
   * Get comprehensive system analytics dashboard data
   */
  async getSystemDashboardData(
    filters: SystemAnalyticsFilters = {}
  ): Promise<SystemOverviewData> {
    try {
      // Get the main dashboard data which contains most of what we need
      const dashboardResponse = await this.getSystemDashboard(filters);
      
      // Extract the data from the nested response structure
      const data = dashboardResponse.data?.data || dashboardResponse.data;
      
      console.log('Dashboard response:', dashboardResponse);
      console.log('Extracted data:', data);
      
      return data;
    } catch (error) {
      console.error('Failed to fetch system dashboard data:', error);
      throw error;
    }
  }

  /**
   * Export system analytics data to CSV/Excel
   */
  async exportSystemAnalytics(
    type: 'csv' | 'excel',
    filters: SystemAnalyticsFilters = {}
  ): Promise<Blob> {
    const params = new URLSearchParams();
    params.append('format', type);
    
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.region) params.append('region', filters.region);

    const response = await api.get(`${this.BASE_URL}/system/export?${params.toString()}`, {
      responseType: 'blob',
    });

    return response.data;
  }

}

const systemAnalyticsService = new SystemAnalyticsService();
export default systemAnalyticsService;